import React from "react";
import Modal from "react-bootstrap/Modal";

const formatAmount = (value) => {
  const amount = Number(value);

  return (Number.isFinite(amount) ? amount : 0).toLocaleString(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2
    }
  );
};

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  })
    .format(date)
    .replace(/\b(am|pm)\b/g, (value) => value.toUpperCase());
};

const statusLabels = {
  pendingAdmin: "Pending CashChecker",
  pendingAccounts: "Pending Accounts",
  approved: "Approved",
  rejectedByAdmin: "Rejected by CashChecker",
  rejectedByAccounts: "Rejected by Accounts"
};

const CashEntryDetailsModal = ({
  entry,
  passbook,
  handleClose
}) => {
  const isCashIn = entry?.entryType === "in";
  const checkerRejected = entry?.status === "rejectedByAdmin";
  const accountsRejected =
    entry?.status === "rejectedByAccounts";

  const creatorName =
    entry?.createdBy?.name ||
    entry?.employee?.name ||
    passbook?.employee?.name ||
    "—";

  const activities = entry
    ? [
        {
          key: "created",
          label: "Created",
          name: creatorName,
          date: entry.createdAt,
          remark: "",
          rejected: false
        },
        ...(entry.adminReviewedBy
          ? [
              {
                key: "checker",
                label: checkerRejected
                  ? "Rejected by CashChecker"
                  : "Approved by CashChecker",
                name: entry.adminReviewedBy.name || "—",
                date: entry.adminReviewedAt,
                remark: entry.adminRemark,
                rejected: checkerRejected
              }
            ]
          : []),
        ...(entry.accountsReviewedBy
          ? [
              {
                key: "accounts",
                label: accountsRejected
                  ? "Rejected by Accounts"
                  : "Approved by Accounts",
                name: entry.accountsReviewedBy.name || "—",
                date: entry.accountsReviewedAt,
                remark: entry.accountsRemark,
                rejected: accountsRejected
              }
            ]
          : [])
      ]
    : [];

  return (
    <Modal
      show={Boolean(entry)}
      onHide={handleClose}
      centered
      scrollable
      size="lg"
      backdrop
      keyboard
      className="cash-entry-details-modal"
      aria-labelledby="cash-entry-details-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="cash-entry-details-title">
          Entry Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {entry && (
          <>
            <div className="cash-entry-details-card">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span
                  className={
                    isCashIn ? "text-success" : "text-danger"
                  }
                >
                  <i
                    className={`fa ${
                      isCashIn ? "fa-plus" : "fa-minus"
                    } me-2`}
                    aria-hidden="true"
                  />
                  {isCashIn ? "Cash In" : "Cash Out"}
                </span>

                <span className="cash-entry-details-date">
                  On {formatDateTime(entry.dateTime)}
                </span>
              </div>

              <div
                className={`cash-entry-details-amount ${
                  isCashIn ? "text-success" : "text-danger"
                }`}
              >
                {formatAmount(entry.amount)}
              </div>

              <div className="cash-entry-details-divider" />

              <div className="cash-entry-details-label">
                Remark
              </div>

              <div className="cash-entry-details-remark">
                {entry.remark || "—"}
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="cash-entry-details-tag cash-entry-category-tag">
                  {entry.category?.name || "—"}
                </span>

                <span className="cash-entry-details-tag cash-entry-payment-tag">
                  {entry.paymentMode === "cash"
                    ? "Cash"
                    : entry.paymentMode === "online"
                      ? "Online"
                      : "—"}
                </span>

                <span
                  className={`badge ${
                    entry.status === "approved"
                      ? "bg-success"
                      : String(entry.status || "").startsWith(
                          "rejected"
                        )
                        ? "bg-danger"
                        : "bg-warning text-dark"
                  }`}
                >
                  {statusLabels[entry.status] ||
                    entry.status ||
                    "—"}
                </span>
              </div>

              <div className="cash-entry-details-divider" />

              <div
                id="cash-entry-custom-information-title"
                className="cash-entry-details-subheading mb-2"
              >
                Custom Information
              </div>

              <div className="table-responsive">
                <table
                  className="table table-bordered table-sm cash-entry-info-table mb-0"
                  aria-labelledby="cash-entry-custom-information-title"
                >
                  <tbody>
                    <tr>
                      <th scope="row">
                        Voucher / Transaction ID
                      </th>
                      <td>{entry.voucherNo || "—"}</td>
                    </tr>

                    {entry.vendor && (
                      <>
                        <tr>
                          <th scope="row">Vendor</th>
                          <td>{entry.vendor.name || "—"}</td>
                        </tr>

                        <tr>
                          <th scope="row">Mobile</th>
                          <td>
                            {entry.vendor.mobileNo || "—"}
                          </td>
                        </tr>
                      </>
                    )}

                    {entry.adminReviewedBy?.name && (
                      <tr>
                        <th scope="row">
                          {checkerRejected
                            ? "Rejected By (CashChecker)"
                            : "Approved By (CashChecker)"}
                        </th>
                        <td>{entry.adminReviewedBy.name}</td>
                      </tr>
                    )}

                    {entry.accountsReviewedBy?.name && (
                      <tr>
                        <th scope="row">
                          {accountsRejected
                            ? "Rejected By (Accounts)"
                            : "Approved By (Accounts)"}
                        </th>
                        <td>
                          {entry.accountsReviewedBy.name}
                        </td>
                      </tr>
                    )}

                    {[
                      [
                        "Balance at Request",
                        entry.balanceAtRequest
                      ],
                      ["Balance Before", entry.balanceBefore],
                      ["Balance After", entry.balanceAfter]
                    ].map(([label, value]) => (
                      <tr key={label}>
                        <th scope="row">{label}</th>
                        <td>
                          {value == null
                            ? "—"
                            : formatAmount(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cash-entry-details-subheading mt-4 mb-3">
              Activities
            </div>

            <div className="cash-entry-activities">
              {activities.map((activity) => (
                <div
                  key={activity.key}
                  className="cash-entry-activity"
                >
                  <span className="cash-entry-activity-icon">
                    <i
                      className={`fa ${
                        activity.key === "created"
                          ? "fa-plus"
                          : activity.rejected
                            ? "fa-times"
                            : "fa-check"
                      }`}
                      aria-hidden="true"
                    />
                  </span>

                  <div className="cash-entry-activity-content">
                    <div>
                      {activity.label} by {activity.name}
                    </div>

                    <div className="text-muted mt-1">
                      On {formatDateTime(activity.date)}
                    </div>

                    {activity.remark && (
                      <div className="mt-1">
                        {activity.remark}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {entry.image?.length > 0 && (
              <div className="mt-4">
                <div className="cash-entry-details-subheading mb-2">
                  Attachments
                </div>

                {entry.image.map((filename, index) => (
                  <div
                    key={`${filename}-${index}`}
                    className="cash-entry-attachment-filename"
                  >
                    <i
                      className="fa fa-paperclip me-2"
                      aria-hidden="true"
                    />
                    {filename}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CashEntryDetailsModal;